import type { Audience, ShareLevel } from "@krut/shared";
import { groupPath } from "../lib/admin-nav.ts";
import { Badge, cx } from "./ui.tsx";

/**
 * The two tiers of shared knowledge, as the admin pages show them: how far a group's memories and skills
 * may travel (its sharing level), and who a shared item reached (its audience).
 */

export const knowledgePath = (groupId: string) => groupPath(groupId, "knowledge");

export const LEVEL_LABEL: Record<ShareLevel, string> = { off: "Off", group: "Within the group", company: "Group and company" };

const LEVEL_HINT: Record<"memory" | "skills", Record<ShareLevel, string>> = {
  memory: {
    off: "Nothing their assistants learn is shared.",
    group: "Work knowledge is shared with the rest of the group only — never company-wide.",
    company: "The group's rules keep team knowledge with the team; everything else that qualifies goes company-wide.",
  },
  skills: {
    off: "Skills stay in each person's own assistant.",
    group: "Clean skills are shared with the rest of the group only — never company-wide.",
    company: "The group's rules keep team skills with the team; other clean skills go company-wide.",
  },
};

export const LEVELS: ShareLevel[] = ["off", "group", "company"];

/** Off / Within the group / Group and company — one row of choices with the meaning of the chosen one underneath. */
export function LevelPicker({ kind, value, onChange }: { kind: "memory" | "skills"; value: ShareLevel; onChange: (v: ShareLevel) => void }) {
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {LEVELS.map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => onChange(l)}
            className={cx("rounded-lg border px-3 py-1.5 text-sm transition", value === l ? "border-[var(--color-accent)]/50 bg-[var(--color-brand)]/10 text-[var(--text)] font-medium" : "border-[var(--line)] hover:bg-[var(--panel-2)]")}
          >
            {LEVEL_LABEL[l]}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-[var(--muted)]">{LEVEL_HINT[kind][value]}</p>
    </div>
  );
}

/** Who a shared memory or skill reached. */
export function AudienceBadge({ audience, groupName, own }: { audience: Audience | null | undefined; groupName?: string | null; own?: boolean }) {
  if (!audience) return null;
  if (audience === "group") return <Badge tone="accent">{own ? "Your group" : groupName ? `Group · ${groupName}` : "Group"}</Badge>;
  return <Badge tone="good">Company</Badge>;
}
